import { getAlpha3CountryCode } from '../country-codes.js';

// Pure currency-conversion for the checkout cart. Extracted from the inline
// secure-checkout app (FET-2766) where it was a component method relying on
// `this`; the per-component state it read (currencyConvertEnabled,
// defaultCountry, countriesList) is now passed explicitly via `ctx` so the
// math is unit-testable. Mutates and returns `cart`, matching prior behavior.
export const convertCurrency = function (cart, conversionRates, countryCode, ctx = {}) {
	if (!cart || typeof cart !== 'object') {
		return cart;
	}

	if (cart.order_offers === undefined || cart.order_offers === null) {
		return cart;
	}

	const { currencyConvertEnabled, defaultCountry, countriesList } = ctx;

	if (!currencyConvertEnabled) {
		countryCode = defaultCountry;
	}

	// Convert alpha-2 to alpha-3 if needed
	if (countryCode && countryCode.length === 2) {
		countryCode = getAlpha3CountryCode(countryCode);
	}

	let conversionKey;
	let conversionRate = 1;
	let conversionCode = 'USD';
	let conversionCurrency = '$';
	for (let key in conversionRates) {
		if (conversionRates[key].country_code === countryCode) {
			conversionKey = key;
			conversionRate = countriesList[key].rate;
			conversionCode = countriesList[key].code;
			conversionCurrency = countriesList[key].symbol;
			break;
		}
	}
	if (conversionRates[conversionKey] === undefined) {
		countryCode = 'USA';
		for (let key in conversionRates) {
			if (conversionRates[key].country_code === countryCode) {
				conversionKey = key;
				conversionRate = countriesList[key].rate;
				conversionCode = countriesList[key].code;
				conversionCurrency = countriesList[key].symbol;
				break;
			}
		}
	}
	for (let offerKey in cart.order_offers) {
		if (cart.order_offers[offerKey].amount_base) {
			cart.order_offers[offerKey].converted_currency_amount = {
				amount: Math.round(cart.order_offers[offerKey].amount_base.amount * conversionRates[conversionKey].rate),
				currency: conversionRates[conversionKey].code,
				formatted: conversionRates[conversionKey].symbol + ((cart.order_offers[offerKey].amount_base.amount / 100) * conversionRates[conversionKey].rate).toFixed(2)
			};
		}
		if (cart.order_offers[offerKey].base_currency_amount) {
			cart.order_offers[offerKey].converted_currency_amount = {
				amount: Math.round(cart.order_offers[offerKey].base_currency_amount.amount * conversionRates[conversionKey].rate),
				currency: conversionRates[conversionKey].code,
				formatted: conversionRates[conversionKey].symbol + ((cart.order_offers[offerKey].base_currency_amount.amount / 100) * conversionRates[conversionKey].rate).toFixed(2)
			};
		}
	}
	for (let shippingKey in cart.available_shipping_categories) {
		if (cart.available_shipping_categories[shippingKey].price_base) {
			cart.available_shipping_categories[shippingKey].price_base.converted = {
				amount: Math.round(cart.available_shipping_categories[shippingKey].price_base.amount.amount * conversionRates[conversionKey].rate),
				currency: conversionRates[conversionKey].code,
				formatted: conversionRates[conversionKey].symbol + ((cart.available_shipping_categories[shippingKey].price_base.amount.amount / 100) * conversionRates[conversionKey].rate).toFixed(2)
			};
		}
	}
	if (cart.shipping_category?.price_base) {
		cart.shipping_category.price_base.converted = {
			amount: Math.round(cart.shipping_category.price_base.amount.amount * conversionRates[conversionKey].rate),
			currency: conversionRates[conversionKey].code,
			formatted: conversionRates[conversionKey].symbol + ((cart.shipping_category.price_base.amount.amount / 100) * conversionRates[conversionKey].rate).toFixed(2)
		};
	}
	if (cart.shipping_category?.price_local) {
		cart.shipping_category.price_local.converted = {
			amount: Math.round(cart.shipping_category.price_local.amount.amount * conversionRates[conversionKey].rate),
			currency: conversionRates[conversionKey].code,
			formatted: conversionRates[conversionKey].symbol + ((cart.shipping_category.price_local.amount.amount / 100) * conversionRates[conversionKey].rate).toFixed(2)
		};
	}
	if (cart.tax_amount_local) {
		cart.tax_amount_local_converted = {
			amount: Math.round(cart.tax_amount_local.amount * conversionRates[conversionKey].rate),
			currency: conversionRates[conversionKey].code,
			formatted: conversionRates[conversionKey].symbol + ((cart.tax_amount_local.amount / 100) * conversionRates[conversionKey].rate).toFixed(2)
		};
	}
	if (cart.tax_amount_base) {
		cart.tax_amount_base_converted = {
			amount: Math.round(cart.tax_amount_base.amount * conversionRates[conversionKey].rate),
			currency: conversionRates[conversionKey].code,
			formatted: conversionRates[conversionKey].symbol + ((cart.tax_amount_base.amount / 100) * conversionRates[conversionKey].rate).toFixed(2)
		};
	}
	if (cart.amount_base) {
		cart.amount_base_converted = {
			amount: Math.round(cart.amount_base.amount * conversionRates[conversionKey].rate),
			currency: conversionRates[conversionKey].code,
			formatted: conversionRates[conversionKey].symbol + ((cart.amount_base.amount / 100) * conversionRates[conversionKey].rate).toFixed(2)
		};
	}
	if (cart.amount_local) {
		cart.amount_local_converted = {
			amount: Math.round(cart.amount_local.amount * conversionRates[conversionKey].rate),
			currency: conversionRates[conversionKey].code,
			formatted: conversionRates[conversionKey].symbol + ((cart.amount_local.amount / 100) * conversionRates[conversionKey].rate).toFixed(2)
		};
	}
	if (cart.amount) {
		cart.amount_converted = {
			amount: Math.round(cart.amount.amount * conversionRates[conversionKey].rate),
			currency: conversionRates[conversionKey].code,
			formatted: conversionRates[conversionKey].symbol + ((cart.amount.amount / 100) * conversionRates[conversionKey].rate).toFixed(2)
		};
	}
	return cart;
};

// Hydrate each entry in `countriesList` with the latest USD exchange rate.
// Network failures are swallowed (rates simply stay at their seeded values),
// matching the inline behavior. Mutates `countriesList` in place.
/**
 * Minor-unit cart total for payment wallets (Apple Pay, PayPal updateOrder).
 * Prefer order/local currency (amount_local) to match Adyen invoice.amount.
 */
export const getCartAmountMinor = (cart) => {
	if (!cart) {
		return 0;
	}

	const raw = cart.amount_local?.amount ?? cart.amount?.amount ?? cart.amount_base?.amount;
	const parsed = parseInt(raw, 10);

	return Number.isFinite(parsed) ? parsed : 0;
};

export const fetchExchangeRates = async (countriesList, fetchImpl = fetch) => {
	try {
		const response = await fetchImpl('https://v6.exchangerate-api.com/v6/46621c57eeff706b26d79877/latest/USD');
		const data = await response.json();

		countriesList.forEach((country) => {
			if (data.conversion_rates[country.code]) {
				country.rate = data.conversion_rates[country.code];
			}
		});
	} catch (error) {
		console.error('Failed to fetch exchange rates', error);
	}
};
